import { Link } from "react-router-dom";
import { Palette, Camera, Truck, Link2, BellRing, PiggyBank, Check } from "lucide-react";
import { SIZES, TEMPLATES, eur, shieldMm } from "../../lib/shield";
import { ShieldPreview } from "../shield/ShieldPreview";
import { SAMPLES } from "../../lib/samples";
import { HowToVideo } from "../HowToVideo";

const Title = ({ eyebrow, children }) => (
  <div className="mb-8"><div className="eyebrow">{eyebrow}</div><h2 className="font-display mt-1 text-3xl font-black uppercase tracking-tight sm:text-4xl">{children}</h2></div>
);

export const HowItWorks = () => (
  <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6" data-testid="home-how-it-works">
    <Title eyebrow="How it works">Three steps to a shield</Title>
    <div className="grid gap-4 md:grid-cols-3">
      {[[Palette, "Pick the look", "Choose a background and your club colours – stripes, splits, geometric or abstract."],
        [Camera, "Add the player", "Upload a photo (we can remove the background), name, position, rating and six stats."],
        [Truck, "Order & we print", "Choose a size, pay securely and we print, cut and deliver your shield."]].map(([Icon, t, d], i) => (
        <Link key={t} to="/design" className="card-lift group block border-2 border-slate-200 bg-white p-6 transition-colors hover:border-slate-900" data-testid={`how-step-${i + 1}-link`}>
          <div className="flex items-center gap-3"><span className="font-cond flex h-9 w-9 items-center justify-center bg-slate-900 text-lg font-bold text-lime-300">{i + 1}</span><Icon className="h-5 w-5" /></div>
          <h3 className="font-display mt-4 text-xl font-extrabold uppercase">{t}</h3><p className="mt-2 text-sm text-slate-600">{d}</p>
          <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold uppercase text-slate-900 underline-offset-4 group-hover:underline">Start designing <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span></span>
        </Link>
      ))}
    </div>
    <div className="mt-12 grid items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
      <HowToVideo id="designer" title="Watch: design a shield in 3 minutes" />
      <div>
        <h3 className="font-display text-2xl font-extrabold uppercase">See how easy it is</h3>
        <p className="mt-2 text-sm text-slate-600">Follow along as we build a shield from scratch – photo, background removal, club colours, stats and size.</p>
        <Link to="/design" className="btn-blaze mt-5 inline-block px-6 py-3 text-sm" data-testid="home-video-design-button">Start designing</Link>
      </div>
    </div>
  </section>
);

const LAYOUT_CARDS = [
  ["columns", "Two columns", "Stats in two neat columns, rating and crest down the left – the classic player card."],
  ["row", "Single row", "All six stats in one line, country code top-right and a bigger centred photo."],
];

export const LayoutCompare = () => (
  <section className="bg-slate-950 py-16 text-white" data-testid="home-layouts">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <div className="eyebrow !text-lime-300">Two card layouts</div>
      <h2 className="font-display mt-1 text-3xl font-black uppercase tracking-tight sm:text-4xl">Pick the style you love</h2>
      <div className="mt-10 grid gap-10 sm:grid-cols-2">
        {LAYOUT_CARDS.map(([id, name, text]) => (
          <div key={id} className="flex flex-col items-center text-center" data-testid={`home-layout-${id}`}>
            <div className="w-[62%] max-w-[260px] drop-shadow-[0_25px_35px_rgba(0,0,0,.55)] transition-transform duration-500 hover:-translate-y-2">
              <ShieldPreview design={{ ...SAMPLES[0], layout: id, player: { ...SAMPLES[0].player, shirtNumber: "8" } }} testId={`home-layout-shield-${id}`} />
            </div>
            <h3 className="font-display mt-6 text-2xl font-extrabold uppercase">{name}</h3>
            <p className="mt-2 max-w-sm text-sm text-slate-300">{text}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 text-center"><Link to="/design" className="btn-blaze inline-block px-6 py-3 text-sm" data-testid="home-layouts-button">Try both in the designer</Link></div>
    </div>
  </section>
);

export const Paths = () => (
  <section className="bg-white py-16" data-testid="home-paths">
    <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-2">
      <div className="border-2 border-slate-900 bg-slate-950 p-8 text-white shadow-[6px_6px_0_0_#a5dd67]" data-testid="home-club-card">
        <div className="eyebrow !text-lime-300">For clubs & schools</div>
        <h3 className="font-display mt-2 text-3xl font-black uppercase">Club sign up</h3>
        <ul className="mt-5 space-y-3 text-sm text-slate-300">
          {[[Palette, "Set your club look once – every player's shield matches"], [Link2, "Share one parent link: parents add their child's photo and stats and pay for their own shield"], [BellRing, "Get notified as each parent finishes"], [PiggyBank, "Raise funds for your club on every shield ordered"]].map(([Icon, t]) => (
            <li key={t} className="flex gap-3"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-lime-300" />{t}</li>
          ))}
        </ul>
        <Link to="/coach?mode=register" className="btn-blaze mt-7 inline-block px-5 py-3 text-sm" data-testid="home-club-card-button">Sign up your club</Link>
      </div>
      <div className="border-2 border-slate-900 bg-white p-8 shadow-[6px_6px_0_0_#0f172a]" data-testid="home-individual-card">
        <div className="eyebrow">For parents, players & fans</div>
        <h3 className="font-display mt-2 text-3xl font-black uppercase">Individual order</h3>
        <ul className="mt-5 space-y-3 text-sm text-slate-600">
          {["No club membership or account needed", "Design it yourself with a live preview", "Save drafts and come back later", "Pay securely and we deliver to your door"].map((t) => (
            <li key={t} className="flex gap-3"><Check className="mt-0.5 h-4 w-4 shrink-0 text-lime-600" />{t}</li>
          ))}
        </ul>
        <Link to="/design" className="mt-7 inline-block bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-700" data-testid="home-individual-card-button">Start designing</Link>
      </div>
    </div>
  </section>
);

export const ShieldWall = () => (
  <section className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.4fr_1fr]" data-testid="home-shield-wall">
    <img src="https://static.prod-images.emergentagent.com/jobs/ce460d56-634b-46e4-9a9a-a11f2d6cc14c/images/8bbf8288afbc94cf1fc43fdb4184ec7820b85f41514dfcaf597bd2f318ba292f.jpeg"
      alt="A personalised player shield hanging on a child's bedroom wall" loading="lazy"
      className="aspect-[3/2] w-full border-2 border-slate-900 object-cover shadow-[8px_8px_0_0_#0f172a]" data-testid="home-shield-wall-photo" />
    <div>
      <div className="eyebrow">On the wall</div>
      <h2 className="font-display mt-1 text-3xl font-black uppercase tracking-tight sm:text-4xl">A keepsake they'll be proud of</h2>
      <p className="mt-4 text-slate-600">Printed in vivid colour on a rigid board and cut to the shield shape. It's ready to hang in the bedroom, the clubhouse or on Granny's wall.</p>
      <Link to="/design" className="mt-6 inline-block bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-700" data-testid="home-shield-wall-button">Design yours</Link>
    </div>
  </section>
);

export const SizesSports = () => (
  <section className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2" data-testid="home-sizes">
    <div>
      <Title eyebrow="Sizes & prices">Four sizes</Title>
      <div className="grid grid-cols-2 gap-3">
        {SIZES.map((s) => (
          <div key={s.id} className="border-2 border-slate-200 bg-white p-4" data-testid={`home-size-${s.id}`}>
            <div className="font-display text-lg font-extrabold uppercase">{s.name}</div>
            <div className="font-mono-cc text-xs text-slate-500">Shield size {shieldMm(s).w} × {s.h} mm</div>
            {s.recommended && <span className="mt-1 bg-lime-400 px-1.5 py-0.5 text-[10px] font-black uppercase text-slate-900" data-testid={`home-recommended-${s.id}`}>Recommended</span>}
            <div className="font-cond mt-2 text-3xl font-bold">{eur(s.price)}</div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500">Prices include VAT.</p>
    </div>
    <div>
      <Title eyebrow="Sport templates">Built for your sport</Title>
      <div className="space-y-3">
        {Object.entries(TEMPLATES).map(([id, t]) => (
          <div key={id} className="flex flex-wrap items-center justify-between gap-2 border-2 border-slate-200 bg-white p-4" data-testid={`home-sport-${id}`}>
            <span className="font-display text-lg font-extrabold uppercase">{t.name}</span>
            <span className="font-cond text-sm font-semibold tracking-wider text-slate-500">{t.stats.map((s) => s.key).join(" · ")}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);
